import { useOutletContext } from 'react-router-dom';
import {
  ADMIN_GENDERS,
  type AdminGender,
  type AdminUser,
  type AdminUserUpdateRequest,
} from '@wlb/shared/api/admin/adminTypes';

/**
 * Every editable field of a member, in one shape.
 *
 * The detail screen splits its editing across sub-modules (identity on the
 * profile tab, activation state on the activation tab) but they all diff against
 * the same snapshot, so a tab can only ever submit the fields it actually owns.
 */
export interface UserFormState {
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  /** `''` means "leave whatever the profile already has"; the API rejects a blank gender. */
  gender: string;
  aboutMe: string;
  isActive: boolean;
  isUserBlocked: boolean;
  isEmailVerified: boolean;
  makeAdmin: boolean;
}

/** The read endpoint answers free text, so only a recognised gender prefills the select. */
export const toGenderOption = (value: string): string =>
  ADMIN_GENDERS.find(gender => gender.toLowerCase() === value.trim().toLowerCase()) ?? '';

export const toUserFormState = (user: AdminUser): UserFormState => ({
  firstName: user.firstName,
  lastName: user.lastName,
  userName: user.userName,
  email: user.email,
  gender: toGenderOption(user.gender),
  aboutMe: user.aboutMe,
  isActive: user.isActive,
  isUserBlocked: user.isUserBlocked,
  isEmailVerified: user.isEmailVerified,
  makeAdmin: user.role === 'Admin',
});

/**
 * Only the fields the operator actually changed are sent: the read endpoint does
 * not return every updatable column, so a full payload would blank out values
 * this screen never saw.
 */
export const changedUserFields = (
  initial: UserFormState,
  next: UserFormState,
): AdminUserUpdateRequest => {
  const payload: AdminUserUpdateRequest = {};
  if (next.firstName !== initial.firstName) {
    payload.firstName = next.firstName;
  }
  if (next.lastName !== initial.lastName) {
    payload.lastName = next.lastName;
  }
  if (next.userName !== initial.userName) {
    payload.userName = next.userName;
  }
  if (next.email !== initial.email) {
    payload.email = next.email;
  }
  if (next.gender !== initial.gender && next.gender) {
    payload.gender = next.gender as AdminGender;
  }
  if (next.aboutMe !== initial.aboutMe) {
    payload.aboutMe = next.aboutMe;
  }
  if (next.isActive !== initial.isActive) {
    payload.isActive = next.isActive;
  }
  if (next.isUserBlocked !== initial.isUserBlocked) {
    payload.isUserBlocked = next.isUserBlocked;
  }
  if (next.isEmailVerified !== initial.isEmailVerified) {
    payload.isEmailVerified = next.isEmailVerified;
  }
  if (next.makeAdmin !== initial.makeAdmin) {
    payload.makeAdmin = next.makeAdmin;
  }
  return payload;
};

/**
 * What the member detail layout hands to its sub-modules.
 *
 * The record is fetched once by the parent route, so switching sub-module does
 * not refetch it and cannot blank the screen — the tabs only swap the panel
 * under the header.
 */
export interface UserDetailContext {
  user: AdminUser;
  /** Refetches the record after a write, keeping the rendered copy meanwhile. */
  reload: () => void;
}

export const useUserDetail = (): UserDetailContext =>
  useOutletContext<UserDetailContext>();
